import { type ReactNode } from "react";
import { brandInitial, masterLogoOf } from "@/lib/homeCatalog";
import { StoreImage } from "@/components/StoreImage";

/**
 * Baris chip merek scroll horizontal. Dipakai DUA mode:
 * - BERANDA (navigasi): `highlightActive={false}` — chip cuma peluncur ke
 *   halaman `/merek` (merek tak lagi memfilter feed beranda).
 * - (Legacy) FILTER: `active`/`onSelect` toggle.
 *
 * Avatar chip memakai LOGO merek (`product_brands.icon_url`) bila tokonya sudah
 * mengunggah; merek tanpa logo tetap jatuh ke avatar inisial. Logo dirender
 * `object-contain` di atas putih — logo grosir fashion lazimnya wordmark
 * lebar/berlatar terang, jadi `object-cover` akan memotong namanya dan latar
 * abu membuatnya nyaris tak terbaca di layar ponsel.
 */
export function BrandRow({
  brands,
  active,
  onSelect,
  highlightActive = true,
  trailing,
  logos
}: {
  brands: string[];
  active: string | null;
  onSelect: (name: string | null) => void;
  /** Sorot chip aktif (mode filter). Mode navigasi beranda → false. */
  highlightActive?: boolean;
  /** Elemen kanan di judul (mis. tautan "Lihat Semua"). */
  trailing?: ReactNode;
  /**
   * Peta nama merek → logo dari `masterLogoMap` (key ternormalisasi). Opsional:
   * pemanggil yang belum punya master merek tetap dapat avatar inisial.
   */
  logos?: ReadonlyMap<string, string>;
}) {
  if (!brands.length) return null;

  return (
    <section className="mt-2 bg-white py-3">
      <div className="mb-2 flex items-center justify-between px-3">
        <h2 className="text-sm font-extrabold text-ink">Merek</h2>
        {trailing}
      </div>
      <div className="no-scrollbar flex gap-2 overflow-x-auto px-3">
        {brands.map((name) => {
          const isActive = highlightActive && active === name;
          const logoUrl = masterLogoOf(logos, name);
          return (
            <button
              key={name}
              type="button"
              onClick={() => onSelect(name)}
              className={`flex h-12 shrink-0 items-center gap-2 rounded-full border pl-1 pr-3.5 text-xs font-bold transition ${
                isActive
                  ? "border-shopee bg-shopeeSoft text-shopee"
                  : "border-neutral-200 bg-white text-ink"
              }`}
            >
              {logoUrl ? (
                <span
                  className={`relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-white ring-1 ${
                    isActive ? "ring-shopee/50" : "ring-neutral-200"
                  }`}
                >
                  <StoreImage
                    src={logoUrl}
                    // Nama merek sudah jadi teks chip di sebelahnya — alt isi
                    // akan dibaca dua kali oleh pembaca layar.
                    alt=""
                    fill
                    sizes="40px"
                    className="object-contain p-0.5"
                  />
                </span>
              ) : (
                <span
                  className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-xs font-extrabold ${
                    isActive ? "bg-shopee text-white" : "bg-neutral-100 text-muted"
                  }`}
                >
                  {brandInitial(name)}
                </span>
              )}
              {name}
            </button>
          );
        })}
      </div>
    </section>
  );
}
